import { mountVeivoValidationFlow, VeivoValidationFlowHandle } from "@veivo/web-sdk";
import { useEffect, useRef, useState } from "react";
import { Alert, Icon, Spinner, Typography } from "@/components/atoms";
import { veivoHandoffUrl } from "@/helpers/veivoHandoffUrl";
import { veivoThemeFromBranding } from "@/helpers/veivoTheme";
import { useOperator } from "@/hooks";
import { useGetVeivoValidationStatusQuery, VeivoValidationFromVin } from "@/redux/apis/vehicle/veivoApi";

interface KyvVerificationFlowProps {
  validation: VeivoValidationFromVin;
  /** Fired once the SERVER reports the vehicle created, with its uuid. */
  onVehicleCreated: (vehicleUuid: string) => void;
}

/**
 * Documents + guided walk-around for a validation RideMinder started (from the
 * plate + state since DEV-1786).
 *
 * Mounts with `resumeValidationId`, so the SDK opens at the documents step
 * rather than at its own start step — the validation already exists at
 * `pending_documents` because RideMinder created it server-side with the tenant
 * API key. That split is deliberate: the plate entry stays on-brand and the
 * camera work, per-capture gating and QR handoff come from the SDK.
 *
 * The VIN is not known when this opens: Veivo reads it off the registration
 * document, and the polled status carries it from then on.
 *
 * Completion is decided by POLLING the server, never by the SDK's client-side
 * callback: the vehicle is created by the webhook handler, so only the server
 * knows when it exists — and only the server can be trusted to say so.
 */
export const KyvVerificationFlow = ({ validation, onVehicleCreated }: KyvVerificationFlowProps) => {
  const { branding } = useOperator();
  const hostRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<VeivoValidationFlowHandle | null>(null);
  const notifiedRef = useRef(false);
  // The SDK's own "verified" screen used to sit here, unmoving, for the several
  // seconds it takes the webhook to create the vehicle — it read as stuck. Once
  // verification is done this component shows its own "creating" state instead.
  const [sdkComplete, setSdkComplete] = useState(false);
  const [slow, setSlow] = useState(false);

  const { data: status } = useGetVeivoValidationStatusQuery(validation.validationId, {
    pollingInterval: 4000,
  });
  // Refused a vehicle: the car is already somebody's. Either at the end
  // (DEV-1754) or mid-flow, as soon as the registration document gave the VIN
  // (DEV-1786) — which spares the operator a walk-around that could never add
  // a vehicle. Final — nothing is being created, so nothing may keep spinning.
  const refused = Boolean(status?.refusalCode);
  const creatingVehicle = (sdkComplete || status?.status === "complete") && !status?.vehicleUuid && !refused;

  useEffect(() => {
    if (!creatingVehicle) return;
    const timer = setTimeout(() => setSlow(true), SLOW_CREATE_MS);
    return () => clearTimeout(timer);
  }, [creatingVehicle]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    flowRef.current = mountVeivoValidationFlow(host, {
      service: { url: validation.veivoBaseUrl },
      // Operator branding, so the flow does not look like a third-party widget.
      theme: veivoThemeFromBranding(branding),
      resumeValidationId: validation.validationId,
      sessionToken: validation.sessionToken,
      // Only known when the validation was started from a VIN; a plate start
      // has none yet. The SDK uses it for its loading copy, so it is read from
      // the START response, never the poll: a VIN arriving mid-flow must not
      // remount the flow and throw away the operator's progress.
      knownVin: validation.vinDecoded?.vin,
      // The phone goes to the public verification page (see veivoHandoffUrl).
      handoffUrl: veivoHandoffUrl,
      // Testing affordance, build-time gated: renders a per-step skip control.
      // The service must ALSO have VEIVO_ALLOW_TEST_SKIP on, so this flag alone
      // cannot bypass anything — and it is unset in production builds.
      allowTestSkip: import.meta.env.VITE_VEIVO_ALLOW_TEST_SKIP === "true",
      onError: (err) => console.error("Veivo flow error:", err),
      onComplete: () => setSdkComplete(true),
    });

    return () => {
      void flowRef.current?.dispose();
      flowRef.current = null;
    };
  }, [
    validation.validationId,
    validation.sessionToken,
    validation.veivoBaseUrl,
    validation.vinDecoded?.vin,
    branding,
  ]);

  useEffect(() => {
    if (notifiedRef.current) return;
    if (!status?.vehicleUuid) return;

    notifiedRef.current = true;
    onVehicleCreated(status.vehicleUuid);
  }, [status?.vehicleUuid, onVehicleCreated]);

  // The poll carries the VIN once the registration document is read.
  const decoded = status?.vinDecoded ?? validation.vinDecoded;
  const vin = decoded?.vin ?? status?.vin;

  return (
    <div className="flex flex-col gap-4">
      {vin && (
        <div className="rounded-lg bg-neutral-gray p-4">
          <Typography variant="paragraph" className="font-semibold">
            {[decoded?.wmi?.manufacturer, decoded?.modelYear].filter(Boolean).join(" · ") || "Vehicle"}
          </Typography>
          <Typography variant="small" className="text-neutral-dark-gray">
            VIN {vin}
          </Typography>
        </div>
      )}

      {/* Surfaced verbatim: it is why the vehicle has not appeared, and it is
          actionable (a missing state, or an unconfigured create token). */}
      {status?.projectionError && !refused && (
        <Typography variant="small" className="text-danger">
          {status.projectionError}
        </Typography>
      )}

      {refused && (
        <Alert
          type="danger"
          message={
            status?.refusalCode === "VEHICLE_ALREADY_IN_FLEET"
              ? "This vehicle is already in your fleet, so it was not added again."
              : "This vehicle is already registered on RideMinder by another operator, so it could not be added. You have not been charged."
          }
        />
      )}

      {creatingVehicle && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-neutral-mid-gray px-6 py-10 text-center" role="status">
          <Icon name="TickCircle" variant="Bold" size="xxl" className="text-success" />
          <Typography variant="h3">Vehicle verified</Typography>
          <div className="flex items-center justify-center text-neutral-dark-gray">
            <Spinner />
            <Typography variant="paragraph">Creating your vehicle…</Typography>
          </div>
          {slow && (
            <Typography variant="small" className="max-w-sm text-neutral-dark-gray">
              This is taking longer than usual. You can close this window — the vehicle will appear in My Vehicles once it is ready.
            </Typography>
          )}
        </div>
      )}

      {/* Kept mounted (the SDK owns it) but hidden once verification is done. */}
      <div ref={hostRef} className={creatingVehicle || refused ? "hidden" : undefined} />
    </div>
  );
};

/** After this long without a vehicle, say so rather than spin in silence. */
const SLOW_CREATE_MS = 30_000;
